import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, useToast } from '@powercore/ui';
import { Check, Zap, Loader2, TrendingUp, AlertCircle, ArrowLeft, AlertTriangle, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../platform';
import { LoadingOverlay } from '../components/LoadingOverlay';

interface BillingStatus {
  subscription: {
    user_id: string;
    plan_id: string;
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    status: string;
    trial_ends_at?: string;
    current_period_start?: string;
    current_period_end?: string;
    cancel_at_period_end?: boolean;
  } | null;
  plan: {
    id: string;
    name: string;
    price_cents: number;
  } | null;
}

interface UsageData {
  feature: string;
  usage: number;
  limit?: number;
  access: boolean;
  percentUsed: number;
}

interface PlanData {
  id: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  billing_interval: string;
  features: Array<{
    feature_id: string;
    feature_name: string;
    limit_value: number;
  }>;
}

export function PricingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [usage, setUsage] = useState<UsageData[]>([]);
  const [availablePlans, setAvailablePlans] = useState<PlanData[]>([]);
  const [trialStatus, setTrialStatus] = useState<{
    isTrial: boolean;
    trialExpired: boolean;
    daysRemaining: number | null;
  } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadPricingData();
    }
  }, [isAuthenticated]);

  const loadPricingData = async () => {
    setLoading(true);
    try {
      const [billingResult, usageResult, plansResult, trialResult] = await Promise.all([
        api.getBillingStatus(),
        api.getBillingUsage(),
        api.getAvailablePlans(),
        api.getTrialStatus().catch(() => null),
      ]);

      if (billingResult.data) {
        const billingData = (billingResult.data as any)?.data || billingResult.data;
        setBillingStatus({
          subscription: billingData.subscription || null,
          plan: billingData.plan || null,
        });
      }

      if (usageResult.data) {
        const rawUsage = (usageResult.data as any)?.data || usageResult.data || [];
        const transformedUsage = rawUsage.map((item: any) => ({
          feature: item.featureName || item.featureId,
          usage: item.usage || 0,
          limit: item.limit,
          access: item.usage < item.limit,
          percentUsed: item.limit > 0 ? (item.usage / item.limit) * 100 : 0,
        }));
        setUsage(transformedUsage);
      }

      if (plansResult.data) {
        setAvailablePlans((plansResult.data as any)?.data || plansResult.data);
      }

      if (trialResult?.data) {
        const trialData = (trialResult.data as any)?.data || trialResult.data;
        setTrialStatus(trialData);
      }
    } catch (err) {
      console.error('Failed to load pricing data:', err);
    }
    setLoading(false);
  };

  // Verify checkout on return from Stripe
  useEffect(() => {
    if (!isAuthenticated) return;
    const upgraded = searchParams.get('upgraded');
    const sessionId = sessionStorage.getItem('mazo-checkout-session-id');

    if (upgraded === 'true' && sessionId) {
      sessionStorage.removeItem('mazo-checkout-session-id');
      searchParams.delete('upgraded');
      setSearchParams(searchParams, { replace: true });

      (async () => {
        try {
          const result = await api.verifyCheckout(sessionId);
          const data = (result.data as any)?.data || result.data;
          if (data?.verified) {
            addToast({
              title: 'Upgrade successful!',
              description: `You're now on the ${data.planId} plan.`,
              variant: 'success',
            });
            loadPricingData();
            window.dispatchEvent(new Event('billing-updated'));
          } else {
            addToast({
              title: 'Payment processing',
              description: 'Your payment is being processed. Refresh in a moment.',
              variant: 'default',
            });
          }
        } catch (err) {
          console.error('Failed to verify checkout:', err);
        }
      })();
    } else if (upgraded === 'true') {
      searchParams.delete('upgraded');
      setSearchParams(searchParams, { replace: true });
    }
  }, [isAuthenticated]);

  const handleUpgradeToPlan = async (planId: string) => {
    setLoadingBilling(true);
    setRedirecting(true);

    try {
      const result = await api.createCheckoutSession(planId);
      const checkoutData = (result.data as any)?.data || result.data;
      const checkoutUrl = checkoutData?.checkoutUrl;

      if (checkoutUrl) {
        const sessionId = checkoutData?.sessionId;
        if (sessionId) {
          sessionStorage.setItem('mazo-checkout-session-id', sessionId);
        }
        window.location.assign(checkoutUrl);
        return;
      }

      console.error('No checkout URL returned. Result:', result);
    } catch (err) {
      console.error('Checkout exception:', err);
    }

    setRedirecting(false);
    setLoadingBilling(false);

    addToast({
      title: 'Checkout unavailable',
      description: 'There was an issue creating the checkout session. Check console for details.',
      variant: 'error',
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-600" />
          <p className="mt-4 text-gray-500" style={{ fontFamily: "'Outfit', sans-serif" }}>Loading plans...</p>
        </div>
      </div>
    );
  }

  const currentPlanId = billingStatus?.subscription?.plan_id ?? 'free';

  const isTrialExpired = trialStatus?.trialExpired === true;

  const plans = availablePlans
    .filter((plan) => plan.id !== 'lite')
    .map((plan) => {
    const price = plan.price_cents === 0
      ? '\u20AC0'
      : `\u20AC${(plan.price_cents / 100).toFixed(0)}`;
    const period = plan.price_cents === 0
      ? 'forever'
      : `/${plan.billing_interval}`;

    const features = plan.features?.map((f) => {
      const isUnlimited = f.limit_value >= 99999;
      if (f.feature_id === 'posts_generated') {
        return isUnlimited ? 'Unlimited posts' : `${f.limit_value} posts / ${plan.billing_interval}`;
      } else if (f.feature_id === 'sources_connected') {
        return isUnlimited ? 'Unlimited sources' : `${f.limit_value} ${f.limit_value === 1 ? 'source' : 'sources'}`;
      }
      return isUnlimited ? `Unlimited ${f.feature_name}` : `${f.feature_name}: ${f.limit_value}`;
    }) || [];

    if (plan.price_cents > 0) {
      features.push('Priority support');
    }

    const highlight = plan.id === 'growth';
    const badge = plan.id === 'growth' ? 'Popular' : null;

    return {
      id: plan.id,
      name: plan.name,
      description: plan.description,
      price,
      period,
      features,
      highlight,
      badge,
    };
  });

  return (
    <div style={{ fontFamily: "'Outfit', sans-serif" }}>
      <LoadingOverlay
        isLoading={redirecting}
        title="Redirecting to checkout..."
        description="You'll be taken to Stripe to complete your upgrade."
        icon="spinner"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Trial Expired Banner */}
        {isTrialExpired && (
          <div className="flex items-center gap-3 p-4 mb-6 bg-red-50 border border-red-200 rounded-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">
                Your free trial has ended. Choose a plan to continue using Mazo.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-mazo-500 hover:bg-mazo-600 text-sm whitespace-nowrap"
              onClick={() => handleUpgradeToPlan('growth')}
              disabled={loadingBilling}
            >
              {loadingBilling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>Upgrade to Growth</>
              )}
            </Button>
          </div>
        )}

        {/* Active Trial Banner */}
        {trialStatus?.isTrial && !trialStatus.trialExpired && trialStatus.daysRemaining != null && (
          <div className="flex items-center gap-3 p-4 mb-6 bg-blue-50 border border-blue-200 rounded-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Clock className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <p className="text-sm font-medium text-blue-900">
              {trialStatus.daysRemaining} {trialStatus.daysRemaining === 1 ? 'day' : 'days'} remaining on your free trial
            </p>
          </div>
        )}

        {/* Back link */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-8 transition-colors"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            {isTrialExpired ? 'Continue with Mazo' : 'Choose Your Plan'}
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            {isTrialExpired
              ? 'Pick the plan that fits your content workflow. Start with Lite for just \u20AC29/mo.'
              : 'Scale your content creation with the right plan for your needs.'}
          </p>
        </div>

        {/* Plan Cards */}
        <div className={`grid grid-cols-1 gap-6 mb-12 ${plans.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
          {plans.map((plan) => {
            const isCurrent = currentPlanId === plan.id;
            const isUpgrade = plan.id !== 'free' && !isCurrent;
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 p-6 transition-all ${
                  isCurrent
                    ? 'border-mazo-500 bg-mazo-50 shadow-md'
                    : plan.highlight
                    ? 'border-mazo-300 bg-white shadow-lg scale-[1.02]'
                    : 'border-gray-200 bg-white shadow-sm'
                }`}
              >
                {plan.badge && !isCurrent && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-mazo-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {plan.badge}
                  </span>
                )}
                {isCurrent && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-mazo-600 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                    <Check className="h-3 w-3" /> Current Plan
                  </span>
                )}

                <div className="mb-5">
                  <p className="text-lg font-semibold text-gray-900">{plan.name}</p>
                  {plan.description && (
                    <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                  )}
                  <div className="flex items-baseline gap-1 mt-3">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-sm text-gray-500">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f: string) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-mazo-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div className="text-center text-sm text-mazo-600 font-medium py-2.5 rounded-lg bg-mazo-100">
                    Current plan
                  </div>
                ) : isUpgrade ? (
                  <Button
                    size="sm"
                    className={`w-full text-sm py-2.5 ${
                      plan.highlight
                        ? 'bg-mazo-500 hover:bg-mazo-600'
                        : 'bg-mazo-500 hover:bg-mazo-600'
                    }`}
                    onClick={() => handleUpgradeToPlan(plan.id)}
                    disabled={loadingBilling}
                  >
                    {loadingBilling ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <><Zap className="h-4 w-4 mr-1.5" />Upgrade</>
                    )}
                  </Button>
                ) : (
                  <div className="text-center text-sm text-gray-400 py-2.5">
                    Free forever
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Usage Stats */}
        {usage.length > 0 && (
          <div className="max-w-md mx-auto">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-gray-400" />
                <p className="text-sm font-semibold text-gray-700">Your Usage This Month</p>
              </div>
              <div className="space-y-4">
                {usage.map((item, index) => (
                  <div key={item.feature || index} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 capitalize">
                        {item.feature?.replace(/_/g, ' ') || 'Unknown'}
                      </span>
                      <span className="font-medium text-gray-900">
                        {item.usage}{item.limit ? (item.limit >= 99999 ? ' / ∞' : ` / ${item.limit}`) : ''}
                      </span>
                    </div>
                    {item.limit && (
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            item.percentUsed >= 90
                              ? 'bg-red-500'
                              : item.percentUsed >= 70
                              ? 'bg-yellow-500'
                              : 'bg-mazo-500'
                          }`}
                          style={{ width: `${Math.min(item.percentUsed, 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Limit exceeded warning */}
              {usage.some((item) => item.percentUsed > 100) && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-4">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">Limit exceeded</p>
                    <p className="text-sm text-red-700 mt-0.5">
                      Upgrade your plan to continue adding sources and generating posts.
                    </p>
                  </div>
                </div>
              )}

              {/* Approaching limit warning */}
              {usage.some((item) => item.percentUsed >= 80 && item.percentUsed <= 100) &&
                !usage.some((item) => item.percentUsed > 100) && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg mt-4">
                  <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Approaching limit</p>
                    <p className="text-sm text-yellow-700 mt-0.5">
                      Consider upgrading to get more posts and sources.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PricingPage;
